import React from "react";
import { useTranslation } from "react-i18next";
import { RouteComponentProps } from "react-router";

import { getSpecConfig } from "../../../../commons/specs";
import { CustomBicycleFilterKey } from "../../../../commons/specs/bicycle";
import { FilterKey } from "../../../../commons/specs/filters";
import { ProductSpecKey, ProductType } from "../../../../commons/specs/product";
import { AssortmentType } from "../../../../commons/types/assortment";
import { GlobalLocationState } from "../../../../commons/types/location";
import Badge from "../../components/Badge/Badge";
import CenteredContent from "../../components/CenteredContent/CenteredContent";
import DetailListItem from "../../components/DetailListItem/DetailListItem";
import DistributedContent from "../../components/DistributedContent/DistributedContent";
import ItemList from "../../components/ItemList/ItemList";
import LoadingIndicator from "../../components/LoadingIndicator/LoadingIndicator";
import MultiColumnItemList from "../../components/MultiColumnItemList/MultiColumnItemList";
import ProductFilterGroupHeadline from "../../components/ProductFilterGroupHeadline/ProductFilterGroupHeadline";
import config from "../../config";
import ModalContainerWithStatePartial from "../../container/Modal/ModalContainerWithStatePartial";
import AvailabilityFilterTeaserModalPartial from "../../container/ProductFinder/AvailabilityFilterTeaserModalPartial";
import FilterModalPartial from "../../container/ProductFinder/FilterModalPartial";
import { getFilterGroupIcon } from "../../libs/filter-icons";
import { getFilterDescription, getFilterGroupLabel, getFilterLabel } from "../../libs/filter-translations";
import { hasActiveFilters, isFilterItemActive, isFilterMarkedNew } from "../../libs/filters";
import { useAppHistory, useAppLocation } from "../../libs/hooks/use-app-routing";
import useAvailabilityFilterTeaser from "../../libs/hooks/use-availability-filter-teaser";
import useGetFilterKeysWithInsufficientFilterValues from "../../libs/hooks/use-get-filter-keys-with-insufficient-filter-values";
import useSelectedBrands from "../../libs/hooks/use-selected-brands";
import useSizingAvailability from "../../libs/hooks/use-sizing-availability";
import { useBrands } from "../../libs/queries/use-brands";
import useEnv from "../../libs/queries/use-env";
import useProductFilter from "../../libs/queries/use-product-filter";
import useProducts from "../../libs/queries/use-products";
import { useSettings } from "../../libs/queries/use-settings";
import useVeloconnectEndpoints from "../../libs/queries/use-veloconnect-endpoints";
import { ROUTES } from "../../routes";
import { useTrackingContext } from "../utils/tracking-context";
import { VeloconnectEndpointsProvider } from "../utils/veloconnect-endpoints-context";

import DetailListItemTooltipPartial from "./DetailListItemTooltipPartial";
import FooterContentPartial from "./FooterContentPartial";

interface Props extends RouteComponentProps<EmptyObject, EmptyObject, GlobalLocationState> {
  isOnline: boolean;
  sizingAvailability: ReturnType<typeof useSizingAvailability>;
  productFilter: ReturnType<typeof useProductFilter>;
}

const DetailsContent = ({ isOnline, sizingAvailability, productFilter }: Props) => {
  const { t, i18n } = useTranslation(["commons"]);
  const location = useAppLocation();
  const history = useAppHistory();
  const { mixpanel } = useTrackingContext();

  const env = useEnv();
  const settings = useSettings();
  const brands = useBrands();
  const veloconnectEndpoints = useVeloconnectEndpoints();
  const availabilityFilterTeaser = useAvailabilityFilterTeaser();
  const selectedBrands = useSelectedBrands({ activeFilters: productFilter.activeFilters });
  const filterKeysWithInsufficientFilterValues = useGetFilterKeysWithInsufficientFilterValues({
    selectedBrands,
    activeFilters: productFilter.activeFilters
  });

  const products = useProducts({
    productType: env.productType,
    activeFilters: productFilter.activeFilters
  });

  const [isAnyFilterModalOpen, setIsAnyFilterModalOpen] = React.useState(false);

  const specConfig = getSpecConfig(env.productType);

  const renderAvailabilityFilterTeaser = (filterKey: FilterKey<ProductType>) => {
    const label = getFilterLabel(
      t,
      env.productType,
      filterKey,
      settings.initialized.customization.modelYearDisplay.term
    );

    return (
      <ModalContainerWithStatePartial
        animationDuration={config.shared.transitionsDurations.fast}
        key={filterKey}
        onOpenChange={isOpen => {
          setIsAnyFilterModalOpen(isOpen);
          if (isOpen) {
            mixpanel?.trackAvailabilityFilterTeaserOpened();
          }
        }}
        modal={(_isOpen, close) => (
          <AvailabilityFilterTeaserModalPartial
            title={label}
            hasCallToAction={availabilityFilterTeaser.hasCallToAction}
            onClose={close}
            onDismiss={availabilityFilterTeaser.dismiss}
          />
        )}
      >
        {(_isOpen, open) => (
          <DetailListItem
            label={label}
            badge={<Badge size="s" variant="accent" label={t("defaults.new")} />}
            onClick={open}
          />
        )}
      </ModalContainerWithStatePartial>
    );
  };

  const renderGroups = () =>
    specConfig.filterGroups.map(group => (
      <React.Fragment key={group.key}>
        <ProductFilterGroupHeadline icon={getFilterGroupIcon(env.productType, group.key)}>
          {getFilterGroupLabel(t, env.productType, group.key)}
        </ProductFilterGroupHeadline>
        <ItemList>
          {group.filters
            .filter(key => key !== ProductSpecKey.Price || settings.initialized.customization.showPrices)
            // Hide assortment filter if device has no current assortment
            .filter(
              key =>
                key !== ProductSpecKey.ManualAssortment ||
                settings.initialized.assortment?.type === AssortmentType.Manual
            )
            .filter(
              key =>
                key !== ProductSpecKey.CombinedAssortment ||
                availabilityFilterTeaser.isVisible ||
                (settings.initialized.assortment?.type === AssortmentType.Automatic &&
                  !settings.assortmentFilterSettings.hideAutomaticAssortmentFilter)
            )
            .filter(
              key =>
                (key as FilterKey<ProductType.Bicycle>) !== CustomBicycleFilterKey.Sizing ||
                settings.isBodySizingEnabled ||
                settings.isBodySizingNotificationsEnabled
            )
            .map(filterKey => {
              if (filterKey === ProductSpecKey.CombinedAssortment && availabilityFilterTeaser.isVisible) {
                return renderAvailabilityFilterTeaser(filterKey);
              }

              const filterConfig = specConfig.filterConfig[filterKey];

              return filterConfig ? (
                <ModalContainerWithStatePartial
                  animationDuration={config.shared.transitionsDurations.fast}
                  key={filterKey}
                  modal={(isOpen, close) => (
                    <FilterModalPartial
                      isFetching={products.query.isLoading}
                      isOpen={isOpen}
                      productType={env.productType}
                      filterKey={filterKey}
                      filterConfig={filterConfig}
                      filterValues={productFilter.query.data ?? {}}
                      activeFilters={productFilter.activeFilters}
                      variantsLength={products.variantsLength}
                      highlightedBrandKeys={brands.highlightedKeys}
                      onFilterChange={productFilter.setActiveFilters}
                      onClose={close}
                      onOpenChange={setIsAnyFilterModalOpen}
                    />
                  )}
                >
                  {(_isOpen, open) => {
                    const label = getFilterLabel(
                      t,
                      env.productType,
                      filterKey,
                      settings.initialized.customization.modelYearDisplay.term
                    );
                    const description = getFilterDescription(t, i18n, env.productType, filterKey);

                    return (
                      <DetailListItemTooltipPartial
                        filterKey={filterKey}
                        filterConfig={filterConfig}
                        filterValues={productFilter.query.data ?? {}}
                        selectedBrands={selectedBrands}
                        activeFilters={productFilter.activeFilters}
                        filterKeysWithInsufficientFilterValues={filterKeysWithInsufficientFilterValues}
                        label={label}
                        description={description}
                        badge={
                          isFilterMarkedNew(filterConfig) ? (
                            <Badge size="s" variant="accent" label={t("defaults.new")} />
                          ) : undefined
                        }
                        active={isFilterItemActive<ProductType>(filterKey, filterConfig, productFilter.activeFilters)}
                        onClick={open}
                        variantsLength={products.variantsLength}
                        isOffline={!isOnline}
                        preventTooltip={isAnyFilterModalOpen}
                        disabledReason={
                          (filterKey as FilterKey<ProductType.Bicycle>) === CustomBicycleFilterKey.Sizing &&
                          settings.isBodySizingEnabled &&
                          !sizingAvailability.available
                            ? t("commons:detailsContentProductFinderOverview.sizingServiceUnavailable")
                            : undefined
                        }
                      />
                    );
                  }}
                </ModalContainerWithStatePartial>
              ) : null;
            })}
        </ItemList>
      </React.Fragment>
    ));

  return (
    <VeloconnectEndpointsProvider endpoints={veloconnectEndpoints}>
      <DistributedContent
        footer={
          <FooterContentPartial
            isLoading={products.query.isLoading}
            activeFilters={productFilter.activeFilters}
            activeFiltersCount={productFilter.activeFiltersCount}
            showsOnlyEBikes={productFilter.showsOnlyEBikes}
            resultCount={products.query.data?.totalFiltered ?? 0}
            onFilterChange={productFilter.setActiveFilters}
            onFilterReset={productFilter.clearAllFilters}
            onSubmit={() => {
              history.push(ROUTES.PRODUCT_FINDER.INDEX, {
                ...(location.state || {}),
                goBackToDetails: true
              });
            }}
            isFilterResetButtonDisabled={!hasActiveFilters(productFilter.activeFilters)}
          />
        }
      >
        <CenteredContent
          childrenWidth={productFilter.query.isFetching ? "auto" : "full"}
          classNames={["u-padding-top-xxxl", "u-space-xxl-media-s-to-l"]}
        >
          {productFilter.query.isFetching ? (
            <LoadingIndicator classNames={["u-padding-top-xxxxl"]} key="loading-indicator" />
          ) : (
            <MultiColumnItemList>{renderGroups()}</MultiColumnItemList>
          )}
        </CenteredContent>
      </DistributedContent>
    </VeloconnectEndpointsProvider>
  );
};

export default DetailsContent;
